import { Search } from '@mui/icons-material'
import { Box } from '@mui/material'
import { ItemFormGroupCheckbox, ItemFormText } from 'components/Form'
import { BreadcrumbsCC, ContainerPage } from 'components/Layout'
import SearchPage from 'components/Layout/SearchPage'
import { ButtonIcon } from 'components/common/Button'
import DateRangePickerCC from 'components/common/DateRangePickerCC'
import useHandleDateRangePicker from 'components/common/DateRangePickerCC/useHandleDateRangePicker'
import { PADDING_TABLE_WRAPPER } from 'constants/ui.contants'
import moment from 'moment'
import { useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { usePageSize } from 'utils/hooks'
import TableMonitorRoom from '../components/TableMonitorRoom'
import { useMonitorRoomListQuery } from '../monitor.service'
import { ROOM_STATUS_OPTIONS } from '../monitor.constant'

const MonitorRoomPage = () => {
    const { t } = useTranslation()
    const { timePicker, setTimePicker } = useHandleDateRangePicker({
        startDate: moment().subtract(7, 'days').startOf('day'),
        endDate: moment()
    })
    const [filter, setFilter] = useState({
        type: ROOM_STATUS_OPTIONS.map(option => option.value),
        roomId: '',
        owner: ''
    })
    const methods = useForm({
        mode: 'onChange',
        defaultValues: filter
    })
    const { page, size, handleChangePage, handleChangeSize } = usePageSize(0, 5)

    const status = filter.type?.length === 1 ? filter.type[0] : ''
    const { start, end } = useMemo(() => ({
        start: moment(timePicker.startDate).startOf('day').unix(),
        end: Math.min(moment(timePicker.endDate).endOf('day').unix(), moment().unix())
    }), [timePicker.startDate, timePicker.endDate])

    const { data: monitorRoomData } = useMonitorRoomListQuery({
        page,
        size,
        type: status,
        roomId: filter.roomId,
        owner: filter.owner,
        start,
        end
    });

    const handleUpdateTimePicker = (nextTimePicker) => {
        setTimePicker(nextTimePicker)
        handleChangePage(0)
    }

    const handleSubmitForm = (values) => {
        setFilter({
            type: values.type || [],
            roomId: values.roomId?.trim() || '',
            owner: values.owner?.trim() || ''
        })
        handleChangePage(0)
    }

    const handleChangeType = (type) => {
        const values = methods.getValues()

        setFilter({
            type: type || [],
            roomId: values.roomId?.trim() || '',
            owner: values.owner?.trim() || ''
        })
        handleChangePage(0)
    }

    return (
        <ContainerPage sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <BreadcrumbsCC />
            <SearchPage sx={{ maxHeight: 'unset' }}>
                <FormProvider {...methods}>
                    <Box
                        component="form"
                        onSubmit={methods.handleSubmit(handleSubmitForm)}
                        sx={{
                            display: 'flex',
                            justifyContent: { xs: 'flex-start', xl: 'space-between' },
                            alignItems: { xs: 'stretch', xl: 'center' },
                            rowGap: 2,
                            columnGap: 2,
                            width: '100%',
                            flexDirection: { xs: 'column', xl: 'row' }
                        }}
                    >
                        <Box
                            sx={{
                                flexShrink: 0,
                                width: { xs: '100%', xl: 'auto' },
                                '& .MuiFormGroup-root': {
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    rowGap: 0.5
                                }
                            }}
                        >
                            <ItemFormGroupCheckbox
                                name={'type'}
                                options={ROOM_STATUS_OPTIONS}
                                onChangeCustom={handleChangeType}
                            />
                        </Box>
                        <Box
                            sx={{
                                display: 'grid',
                                alignItems: 'center',
                                gap: 2,
                                width: { xs: '100%', xl: 'auto' },
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'minmax(270px, 1fr) minmax(220px, 1fr)',
                                    lg: '270px 250px 250px auto'
                                },
                                '& .daterange-container': {
                                    width: '100%',
                                    minWidth: 0
                                },
                                '& .input-daterange': {
                                    width: '100%'
                                }
                            }}
                        >
                            <DateRangePickerCC
                                sx={{ minWidth: 0 }}
                                inputId="input-daterange-monitor-room"
                                timePicker={timePicker}
                                setTimePicker={handleUpdateTimePicker}
                                positionTooltip="bottom-end"
                                initialSettingsProps={{
                                    maxDate: moment()
                                }}
                            />
                            <ItemFormText
                                name={'roomId'}
                                placeholder={t('common:placeholder.enterRoomId')}
                                sx={{ width: '100%' }}
                            />
                            <ItemFormText
                                name={'owner'}
                                placeholder={t('common:placeholder.enterUsername')}
                                sx={{ width: '100%' }}
                            />
                            <ButtonIcon
                                type="submit"
                                startIcon={<Search sx={{ color: 'white' }} />}
                                sx={{
                                    width: { xs: '100%', lg: 'auto' },
                                    minWidth: { lg: 120 },
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {t('actions.search')}
                            </ButtonIcon>
                        </Box>
                    </Box>
                </FormProvider>
            </SearchPage>
            <Box sx={{ p: PADDING_TABLE_WRAPPER }}>
                <TableMonitorRoom
                    tableData={monitorRoomData?.data}
                    page={page}
                    size={size}
                    handleChangePage={handleChangePage}
                    handleChangeLimit={handleChangeSize}
                />
            </Box>
        </ContainerPage>
    )
}

export default MonitorRoomPage
